import Phaser from 'phaser';

/**
 * Centralised sound-effect playback.
 *
 * Used by BattleScene (hit, crit, spell cast, death, pickups) and other scenes
 * (menu click/hover, scene open/close). Volume is shared with the BGM slider,
 * scaled down so SFX don't overpower the music.
 *
 * Usage:
 *   this.sfx = new SoundManager(this);
 *   this.sfx.play('ui_click');
 *   this.sfx.play('hit_medium', { volume: 0.8 });
 *
 * Rate-limit guard: repeated plays of the same key within RATE_LIMIT_MS
 * are suppressed so rapid auto-attacks don't create a wall of noise.
 */
export class SoundManager {
  private scene: Phaser.Scene;
  /** Last play timestamp per key — used to throttle rapid repeats */
  private lastPlay: Map<string, number> = new Map();
  /** Shared SFX volume multiplier (0..1). Tied to the music volume for now. */
  static sfxVolume = 0.45;
  static muted = false;
  /** Min ms between two plays of the same key */
  private readonly RATE_LIMIT_MS = 40;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /**
   * Short key → full texture key ('hit_medium' → 'sfx_hit_medium').
   * Friendly names at call sites, stable storage keys in the cache.
   */
  play(shortKey: string, config: Phaser.Types.Sound.SoundConfig = {}): void {
    if (SoundManager.muted) return;
    const key = shortKey.startsWith('sfx_') ? shortKey : `sfx_${shortKey}`;
    if (!this.scene.cache.audio.exists(key)) return;

    const now = Date.now();
    const last = this.lastPlay.get(key) ?? 0;
    if (now - last < this.RATE_LIMIT_MS) return;
    this.lastPlay.set(key, now);

    const volume = (config.volume ?? 1) * SoundManager.sfxVolume;
    try {
      this.scene.sound.play(key, { ...config, volume });
    } catch {
      /* audio system not ready or key missing — ignore */
    }
  }

  /** Pick one of a list of keys at random (for variation — e.g. hit variants) */
  playRandom(shortKeys: string[], config: Phaser.Types.Sound.SoundConfig = {}): void {
    if (shortKeys.length === 0) return;
    const pick = shortKeys[Math.floor(Math.random() * shortKeys.length)];
    this.play(pick, config);
  }
}
